import React, { useEffect, useState } from 'react';
import ImageGradient from './ImageGradient';
import { colors } from '../../theme';

/**
 * Full-width photo band that dissolves into Cipher navy — on every edge,
 * not just the bottom. The sharp photo is vignette-masked so all four
 * sides fade to transparent, revealing the same blurred/dimmed photo
 * already sitting behind it on the page (see LoginPage's ambient backdrop)
 * instead of stopping at a hard rectangle. `children` render inside the
 * gradient's fade zone at the bottom, where the color overlay still gives
 * full contrast for text.
 * Plays the item-7 entrance once per mount: photo settles from 1.03x scale,
 * content fades in ~120ms behind it.
 */
const CipherHero = ({ image, height = 'clamp(340px, 54vh, 520px)', focalPosition = 'center 22%', direction = 'down', vignette = true, children }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div style={{ position: 'relative', height, overflow: 'hidden', background: colors.bg }}>
      <style>{`
        .cipher-hero-photo {
          position: absolute; inset: 0;
          background-size: cover;
          transform: scale(1.03);
          opacity: 0;
          transition: transform 650ms cubic-bezier(0.16, 1, 0.3, 1), opacity 650ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cipher-hero-photo.is-entered { transform: scale(1); opacity: 1; }
        .cipher-hero-photo.has-vignette {
          /* Fully opaque out to more than half the box — only the true
             edges/corners soften. Earlier version faded from 18%, which
             left most of the photo semi-transparent and hard to read. */
          mask-image: radial-gradient(ellipse 88% 85% at 50% 42%, black 55%, transparent 100%);
          -webkit-mask-image: radial-gradient(ellipse 88% 85% at 50% 42%, black 55%, transparent 100%);
          mask-repeat: no-repeat; -webkit-mask-repeat: no-repeat;
          mask-size: 100% 100%; -webkit-mask-size: 100% 100%;
        }
        .cipher-hero-content {
          opacity: 0;
          transform: translateY(8px);
          transition: opacity 500ms cubic-bezier(0.16, 1, 0.3, 1) 150ms, transform 500ms cubic-bezier(0.16, 1, 0.3, 1) 150ms;
        }
        .cipher-hero-content.is-entered { opacity: 1; transform: translateY(0); }
        @media (prefers-reduced-motion: reduce) {
          .cipher-hero-photo, .cipher-hero-content { transition: none; transform: none; opacity: 1; }
        }
      `}</style>
      <div
        className={`cipher-hero-photo${entered ? ' is-entered' : ''}${vignette ? ' has-vignette' : ''}`}
        style={{ backgroundImage: `url(${image})`, backgroundPosition: focalPosition }}
      />
      <ImageGradient direction={direction} />
      {children && (
        <div
          className={`cipher-hero-content${entered ? ' is-entered' : ''}`}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            padding: '0 24px 28px',
            textAlign: 'center',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default CipherHero;
